import { FileTypeParser } from 'file-type';
import { detectXml } from '@file-type/xml';

const getAcceptTokens = (accept: string): string[] =>
    accept
        .split(',')
        .map((type) => type.trim().toLowerCase())
        .filter(Boolean);

const extensionAliases: Record<string, string> = {
    jpg: 'jpeg',
    tif: 'tiff'
};

const normalizeExtension = (extension: string): string => extensionAliases[extension] || extension;

const extensionToMimeMap: Record<string, string[]> = {
    png: ['image/png'],
    jpeg: ['image/jpeg'],
    webp: ['image/webp'],
    svg: ['image/svg+xml'],
    pdf: ['application/pdf'],
    eps: ['application/eps', 'application/postscript'],
    zip: ['application/zip'],
    csv: ['text/csv'],
    ttf: ['font/ttf', 'font/sfnt', 'application/font-sfnt'],
    otf: ['font/otf']
};

const getAllowedMimeTypes = (accept: string): string[] => {
    const explicitMimes = getAcceptTokens(accept).filter((type) => type.includes('/'));
    const extensionMimes = getAcceptTokens(accept)
        .filter((type) => type.startsWith('.'))
        .map((type) => normalizeExtension(type.replace(/^\./, '')))
        .map((extension) => extensionToMimeMap[extension])
        .filter(Boolean)
        .flat();

    return [...new Set([...explicitMimes, ...extensionMimes])];
};

const mimeTypeMatches = (detectedMime: string, acceptedMime: string): boolean => {
    if (acceptedMime.endsWith('/*')) {
        const acceptedPrefix = acceptedMime.slice(0, acceptedMime.indexOf('/') + 1);
        return detectedMime.startsWith(acceptedPrefix);
    }

    return detectedMime === acceptedMime;
};

const parser = new FileTypeParser({ customDetectors: [detectXml] });

export const isValidFileSignature = async (file: File, accept: string): Promise<boolean> => {
    const acceptTokens = getAcceptTokens(accept);
    const allowedMimeTypes = getAllowedMimeTypes(accept);

    if (!acceptTokens.length || accept == '*') return true;
    if (!allowedMimeTypes.length) return false;
    if (!(file instanceof File)) return false;

    try {
        const detectedType = await parser.fromBlob(file);
        if (!detectedType) {
            return false;
        }

        const detectedMime = detectedType.mime.toLowerCase();

        return allowedMimeTypes.some((acceptedMime) => mimeTypeMatches(detectedMime, acceptedMime));
    } catch {
        return false;
    }
};
